import React, { useCallback, useMemo } from "react";
import classNames from "classnames";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import {
    AccountProxyObject,
    ValidationErrors,
} from "./CreateAccountSpreadsheetModal";
import styles from "./Footer.module.scss";

interface Props {
    loading: boolean;
    addLines: () => void;
    onSave: () => void;
    accounts: AccountProxyObject[];
    validationErrors: ValidationErrors;
}

export const Footer: React.FC<Props> = ({
    loading,
    addLines,
    onSave,
    accounts,
    validationErrors,
}) => {
    const onSubmit = useCallback(() => {
        onSave();
    }, [onSave]);

    const accountSummary = useMemo(() => {
        const accountsWithName = accounts.filter(
            (account) => account.name?.trim() !== "",
        );
        const mappedToExisting = accountsWithName.filter(
            (account) => account.mappedKickAccountId,
        );
        const newAccounts = accountsWithName.filter(
            (account) => !account.mappedKickAccountId,
        );
        const invalidNewAccounts = newAccounts.filter(
            (account) => !account.accountType,
        );

        return {
            total: accountsWithName.length,
            mappedToExisting: mappedToExisting.length,
            newAccounts: newAccounts.length,
            invalidNewAccounts: invalidNewAccounts.length,
        };
    }, [accounts]);

    const shownError = useMemo(() => {
        if (validationErrors.noAccounts) {
            return validationErrors.noAccounts;
        }

        if (validationErrors.accounts && validationErrors.accounts.length > 0) {
            const accountErrorLines: string[] = [];

            validationErrors.accounts.forEach((account) => {
                if (account.name) {
                    accountErrorLines.push(account.name);
                }
                if (account.accountType) {
                    accountErrorLines.push(account.accountType);
                }
            });

            if (accountErrorLines.length > 1) {
                return "Please complete the highlighted fields";
            }

            if (accountErrorLines.length === 1) {
                return accountErrorLines[0];
            }
        }

        return undefined;
    }, [validationErrors]);

    return (
        <div className={styles.modalFooter}>
            {accountSummary.total > 0 && (
                <div className={styles.summary}>
                    <div className={styles.summaryStats}>
                        <div className={styles.summaryItem}>
                            <span className={styles.summaryLabel}>
                                Total accounts:
                            </span>
                            <span className={styles.summaryValue}>
                                {accountSummary.total}
                            </span>
                        </div>
                        <div className={styles.summaryItem}>
                            <span className={styles.summaryLabel}>
                                Mapped to existing:
                            </span>
                            <span className={styles.summaryValue}>
                                {accountSummary.mappedToExisting}
                            </span>
                        </div>
                        <div className={styles.summaryItem}>
                            <span className={styles.summaryLabel}>
                                New accounts to create:
                            </span>
                            <span className={styles.summaryValue}>
                                {accountSummary.newAccounts}
                            </span>
                        </div>
                        {accountSummary.invalidNewAccounts > 0 && (
                            <div
                                className={classNames(
                                    styles.summaryItem,
                                    styles.summaryError,
                                )}
                            >
                                <span className={styles.summaryLabel}>
                                    Missing type:
                                </span>
                                <span className={styles.summaryValue}>
                                    {accountSummary.invalidNewAccounts}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            )}
            <div className={styles.footerButtons}>
                <div>
                    <Button
                        size="md"
                        type="button"
                        variant="primary"
                        onClick={addLines}
                    >
                        Add lines
                    </Button>
                </div>

                <HStack align="center" gap={12}>
                    {shownError && (
                        <p className={styles.footerError}>{shownError}</p>
                    )}
                    <ButtonWithLoader
                        size="md"
                        loading={loading}
                        disabled={!!shownError}
                        variant="primary"
                        onClick={onSubmit}
                        type="submit"
                        hotKey="mod+enter"
                        hotKeyOptions={{
                            description: "Save Chart of Accounts",
                        }}
                    >
                        Save
                    </ButtonWithLoader>
                </HStack>
            </div>
        </div>
    );
};
